/**
 * ТЗ 0093 — адреса раздела «Настройки»: построители ссылок и разбор старых
 * адресов, которые переехали в хаб.
 *
 * Модуль без React, i18n и ролей — одни строки и `PATHS`, чтобы его могли
 * брать и страницы, и чистые модели (`smsModel`, `leadsModel`) без риска
 * циклического импорта. Всё здесь проверяется юнит-тестами.
 *
 * 🔴 Ссылка на раздел настроек — ТОЛЬКО отсюда. Путь, собранный шаблонной
 * строкой по месту, — это 404, которого не видят ни tsc, ни сборка, ни
 * тесты: так уже было с карточкой группы.
 */

import { PATHS } from 'router/config';

/** Вкладка раздела — в адресе `?tab=`; первая в списке — по умолчанию. */
export const SETTINGS_TAB_PARAM = 'tab';

/** Вкладка из адреса; незнакомая или пустая — первая из `tabs`. */
export const parseSettingsTab = <T extends string>(
  search: string,
  tabs: readonly T[],
): T => {
  const value = new URLSearchParams(search).get(SETTINGS_TAB_PARAM);
  return tabs.find((tab) => tab === value) ?? tabs[0];
};

/**
 * Адрес со вкладкой. Первая вкладка (по умолчанию) параметр удаляет:
 * `/settings/sms`, а не `/settings/sms?tab=settings`.
 */
export const withSettingsTab = <T extends string>(
  search: string,
  tab: T,
  tabs: readonly T[],
): string => {
  const params = new URLSearchParams(search);
  if (tab === tabs[0]) params.delete(SETTINGS_TAB_PARAM);
  else params.set(SETTINGS_TAB_PARAM, tab);
  return params.toString();
};

const withQuery = (pathname: string, params: URLSearchParams): string => {
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

/* ── SMS ────────────────────────────────────────────────────────────── */

/** Вкладки раздела «SMS»: канал и адреса ссылок, лесенки напоминаний. */
export const SETTINGS_SMS_TABS = ['settings', 'plans'] as const;
export type SettingsSmsTab = typeof SETTINGS_SMS_TABS[number];

export const smsSettingsHref = (tab: SettingsSmsTab = 'settings'): string =>
  withQuery(
    PATHS.settingsSms,
    new URLSearchParams(withSettingsTab('', tab, SETTINGS_SMS_TABS)),
  );

/* ── Шаблоны сообщений ──────────────────────────────────────────────── */

export interface ITemplatesTarget {
  /** Тема шаблонов (`SmsTopic`): вкладка раздела откроется на ней. */
  topic?: string | null;
  /** Код шаблона: окно правки этого шаблона откроется сразу. */
  template?: string | null;
  /**
   * ТЗ 0094: вкладка «Мои» — личные шаблоны сотрудника; с `template` —
   * окно его версии этого шаблона (нет своей — «Сделать свою версию»).
   */
  mine?: boolean;
}

/** ТЗ 0094: параметр вкладки «Мои» в адресе шаблонов. */
export const TEMPLATES_MINE_PARAM = 'mine';

/**
 * «Настройки → Шаблоны сообщений» (`?topic=` и `&template=` читает сама
 * вкладка шаблонов). Одна функция на все входящие ссылки: лесенки, окно
 * «Поставить на лесенку», напоминания договора, кнопка «Шаблоны» в
 * договорах, меню и палитра.
 */
export const templatesHref = ({
  topic,
  template,
  mine,
}: ITemplatesTarget = {}): string => {
  const params = new URLSearchParams();
  if (topic) params.set('topic', topic);
  if (template) params.set('template', template);
  if (mine) params.set(TEMPLATES_MINE_PARAM, '1');
  return withQuery(PATHS.settingsTemplates, params);
};

/* ── Интеграции ─────────────────────────────────────────────────────── */

/** Вкладки раздела «Интеграции». Какие видны — решают роли (реестр). */
export const INTEGRATION_TABS = ['kommo', 'slack', 'zoom'] as const;
export type IntegrationTab = typeof INTEGRATION_TABS[number];

export const integrationsHref = (tab?: IntegrationTab): string => {
  const params = new URLSearchParams();
  if (tab) params.set(SETTINGS_TAB_PARAM, tab);
  return withQuery(PATHS.settingsIntegrations, params);
};

/* ── Письма дофинансирования ────────────────────────────────────────── */

/**
 * «Настройки → Письма дофинансирования»: робот рассылок, отправитель, час
 * отправки, правила «за N дней» и календарь. Сюда ведут строка «Автоподбор
 * стоит» (очередь и заявки дофина) и старая вкладка `?tab=mailRules`.
 */
export const dofinMailHref = (): string => PATHS.settingsDofinMail;

/* ── Маркетинг ──────────────────────────────────────────────────────── */

/**
 * Вкладки «Настройки → Маркетинг» — справочники, по которым сервер
 * раскладывает лиды и продажи: правила каналов, офферов и сопоставления
 * кампаний; семейства курсов; маршрут «сигналы → задачи», бренд, события GA4
 * и хосты сайта; типы воронок Kommo. Первая — по умолчанию (сюда ведёт и
 * «Создать правило» у источника в «Качестве данных»).
 */
export const SETTINGS_MARKETING_TABS = [
  'channels',
  'courses',
  'signals',
  'pipelines',
] as const;
export type SettingsMarketingTab = typeof SETTINGS_MARKETING_TABS[number];

export const marketingSettingsHref = (
  tab: SettingsMarketingTab = 'channels',
): string =>
  withQuery(
    PATHS.settingsMarketing,
    new URLSearchParams(withSettingsTab('', tab, SETTINGS_MARKETING_TABS)),
  );

/* ── Финансы ────────────────────────────────────────────────────────── */

/**
 * Вкладки «Настройки → Финансы»: правила и параметры cash flow (по
 * умолчанию) и параметры платёжного календаря. Курсы валют и остаток денег
 * остались в ящике календаря — это данные на дату, а не настройка.
 */
export const SETTINGS_FINANCE_TABS = ['cashflow', 'calendar'] as const;
export type SettingsFinanceTab = typeof SETTINGS_FINANCE_TABS[number];

export const financeSettingsHref = (
  tab: SettingsFinanceTab = 'cashflow',
): string =>
  withQuery(
    PATHS.settingsFinance,
    new URLSearchParams(withSettingsTab('', tab, SETTINGS_FINANCE_TABS)),
  );

/* ── Карьерный центр ────────────────────────────────────────────────── */

/** «Настройки → Карьерный центр»: тексты постов для репоста. */
export const careerSettingsHref = (): string => PATHS.settingsCareer;

/* ── Робот блокировки должников ─────────────────────────────────────── */

/**
 * «Настройки → Робот блокировки должников» (ТЗ 0074): включение робота,
 * порог, письмо, Slack и SMS студенту. Сюда ведёт строка «второй
 * выключатель» у ключей `LMS_BLOCK_*` в «Роботах и расписаниях».
 */
export const lmsAccessHref = (): string => PATHS.settingsLmsAccess;

/* ── Роботы и расписания ────────────────────────────────────────────── */

/**
 * Вкладки «Роботов и расписаний», которые знает фронт. Вкладки ключей —
 * разделы runtime-настроек, их список и порядок отдаёт сервер
 * (`runtimeSettingSections`, кроме `ai`); за ними всегда идут расписания
 * задач и журнал изменений. Первая вкладка — `robots`: у неё адрес без
 * `?tab=` (сервер ставит раздел роботов первым).
 */
export const ROBOTS_DEFAULT_TAB = 'robots';
export const ROBOTS_FIXED_TABS = ['schedules', 'changes'] as const;
export type RobotsFixedTab = typeof ROBOTS_FIXED_TABS[number];

export const robotsSettingsHref = (
  tab: string = ROBOTS_DEFAULT_TAB,
): string => {
  const params = new URLSearchParams();
  if (tab && tab !== ROBOTS_DEFAULT_TAB) params.set(SETTINGS_TAB_PARAM, tab);
  return withQuery(PATHS.settingsRobots, params);
};

/* ── ИИ ─────────────────────────────────────────────────────────────── */

/** «Настройки → ИИ»: модели и включение платных ИИ-функций. */
export const aiSettingsHref = (): string => PATHS.settingsAi;

/* ── Старые адреса ──────────────────────────────────────────────────── */

/**
 * `/sms?tab=settings|plans|templates` — вкладки, которые уехали в хаб. Эти
 * адреса вшиты в задачи, уведомления и закладки, поэтому страница SMS
 * уводит с них сама (react-router v5 не матчит query). Параметры шаблонов
 * (`topic`, `template`) переезжают вместе с адресом; фильтры журнала — нет,
 * их в настройках нет. Не старый адрес — `null`.
 */
export const legacySmsTabTarget = (search: string): string | null => {
  const params = new URLSearchParams(search);
  switch (params.get(SETTINGS_TAB_PARAM)) {
    case 'settings':
      return smsSettingsHref('settings');
    case 'plans':
      return smsSettingsHref('plans');
    case 'templates':
      return templatesHref({
        topic: params.get('topic'),
        template: params.get('template'),
      });
    default:
      return null;
  }
};

/**
 * `/leads?tab=kommo` — вкладка Admin «Kommo» уехала в «Интеграции». Уводим
 * только того, кому раздел виден: продавец по такой ссылке, как и раньше,
 * остаётся в списке лидов.
 */
export const legacyLeadsTabTarget = (
  search: string,
  canSeeKommo: boolean,
): string | null =>
  canSeeKommo && new URLSearchParams(search).get(SETTINGS_TAB_PARAM) === 'kommo'
    ? integrationsHref('kommo')
    : null;

/**
 * `/dofinansowanie?tab=mailRules` — вкладка «Правила писем» уехала в хаб.
 * Прочие параметры адреса едут следом (`rule=` и то, что появится у
 * раздела), `tab` — нет: у раздела настроек своя вкладка не нужна. Уводит
 * сама страница дофина: роли её маршрута и раздела совпадают
 * (`DOFIN_READ_ROLES`). Не старый адрес — `null`.
 */
export const legacyDofinTabTarget = (search: string): string | null => {
  const params = new URLSearchParams(search);
  if (params.get(SETTINGS_TAB_PARAM) !== 'mailRules') return null;
  params.delete(SETTINGS_TAB_PARAM);
  return withQuery(dofinMailHref(), params);
};
